import { VIEWPORT_H, VIEWPORT_W } from '../constants';
import { hudLayoutScale, hudViewport } from '../hud-viewport';

/** Native art sizes for the bottom navigation bar. */
export const BOTTOM_MENU = {
  barW: 1080,
  barH: 168,
  secondaryBgW: 403,
  secondaryBgH: 532,
  mainBgW: 819,
  mainBgH: 744,
  /** Extra shrink on top of responsive HUD scale. */
  menuScale: 0.52,
  /** Additional scale for the center main button (0.8 = 20% smaller). */
  mainScale: 0.8,
  /** Bar height as a fraction of the secondary button height. */
  barHeightOfSecondary: 0.6,
  secondaryIconY: 0.55,
  mainIconY: 0.5,
  secondaryIconScale: 0.6,
  mainIconScale: 0.675,
  mainGeodeIconScale: 0.4,
  /** Resting tabs sit this far below the bottom; selected tab rises flush to the edge. */
  tabTravel: 40,
} as const;

export const BOTTOM_MENU_BUTTONS = ['rank', 'booga', 'main', 'weapon', 'shop'] as const;
export type BottomMenuButtonKey = (typeof BOTTOM_MENU_BUTTONS)[number];

export function menuSelectionForPanel(panel: 0 | 1): BottomMenuButtonKey {
  return panel === 0 ? 'booga' : 'main';
}

export function menuTabTravel() {
  return Math.round(BOTTOM_MENU.tabTravel * bottomMenuScale());
}

export function bottomMenuScale() {
  return hudLayoutScale() * BOTTOM_MENU.menuScale;
}

/** Main stays centered; secondary buttons fan out with even gaps. */
function centerAnchoredSlotCenters(viewW: number, secondaryW: number, mainW: number) {
  const gap = Math.max(0, (viewW - mainW - secondaryW * 4) / 6);
  const mainX = viewW / 2;
  const boogaX = mainX - mainW / 2 - gap - secondaryW / 2;
  const rankX = boogaX - secondaryW / 2 - gap - secondaryW / 2;
  const weaponX = mainX + mainW / 2 + gap + secondaryW / 2;
  const shopX = weaponX + secondaryW / 2 + gap + secondaryW / 2;
  return { rank: rankX, booga: boogaX, main: mainX, weapon: weaponX, shop: shopX };
}

export type BottomMenuLayout = {
  barX: number;
  barY: number;
  barW: number;
  barH: number;
  secondaryW: number;
  secondaryH: number;
  mainW: number;
  mainH: number;
  slots: Record<BottomMenuButtonKey, { x: number; y: number; iconY: number; iconScale: number; geodeIconScale?: number }>;
};

export function bottomMenuLayout(): BottomMenuLayout {
  const scale = bottomMenuScale();
  const viewW = hudViewport.width || VIEWPORT_W;
  const viewH = hudViewport.height || VIEWPORT_H;

  const secondaryW = Math.round(BOTTOM_MENU.secondaryBgW * scale);
  const secondaryH = Math.round(BOTTOM_MENU.secondaryBgH * scale);
  const mainW = Math.round(BOTTOM_MENU.mainBgW * scale * BOTTOM_MENU.mainScale);
  const mainH = Math.round(BOTTOM_MENU.mainBgH * scale * BOTTOM_MENU.mainScale);

  const barH = Math.round(secondaryH * BOTTOM_MENU.barHeightOfSecondary);
  const barAspect = BOTTOM_MENU.barW / BOTTOM_MENU.barH;
  const barW = Math.max(viewW, Math.round(barH * barAspect));
  const barX = (viewW - barW) / 2;
  const barY = viewH - barH;

  const slotY = viewH;
  const centers = centerAnchoredSlotCenters(viewW, secondaryW, mainW);
  const slots = {} as BottomMenuLayout['slots'];

  BOTTOM_MENU_BUTTONS.forEach((key) => {
    const isMain = key === 'main';
    slots[key] = {
      x: centers[key],
      y: slotY,
      iconY: slotY - (isMain ? mainH : secondaryH) * (isMain ? BOTTOM_MENU.mainIconY : BOTTOM_MENU.secondaryIconY),
      iconScale: isMain ? BOTTOM_MENU.mainIconScale : BOTTOM_MENU.secondaryIconScale,
      geodeIconScale: isMain ? BOTTOM_MENU.mainGeodeIconScale : undefined,
    };
  });

  return { barX, barY, barW, barH, secondaryW, secondaryH, mainW, mainH, slots };
}
